import React from "react";
import { PageBuilderCustomOfferBoxFragment } from "types/graphql-types";
import { graphql } from "gatsby";
import { matchesAny } from "src/utils/localization/geolocation";
import {
  SectionContainer,
  SectionTitleBody,
  SectionTitleHeading,
  SectionTitle,
  isBlockContentValuePresent
} from "@silversea-ssc/ui-components-react";
import { BackgroundColor } from "types/componentBackground";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import OfferHighlights, {
  OfferHighlight
} from "components/OfferHighlights/OfferHighlights";
import TextBox from "components/TextBox/TextBox";
import { isPresent } from "src/utils/checks";
import { cardColorForBackground } from "./pageBuilderRenderHelper";
import { useRemotePromos } from "src/ssc-api/useRemotePromos";
import { byConfigurationCode } from "src/utils/promos";

export const PageBuilderCustomOfferBoxSectionContainer: React.FC<
  React.PropsWithChildren<{
    backgroundColor: BackgroundColor;
    section: PageBuilderCustomOfferBoxFragment;
  }>
> = ({ backgroundColor, section }) => {
  const geoLocation = useGeolocation();
  const visible = matchesAny(section.locationFilters, geoLocation);
  const cardBackgroundColor = cardColorForBackground(backgroundColor);

  if (!visible) return null;

  return (
    <SectionContainer
      spacingTop="compact"
      spacingBottom="compact"
      backgroundColor={backgroundColor}
      dataContentName="AccordionText_section"
    >
      <PageBuilderCustomOfferBox
        {...section}
        cardBackgroundColor={cardBackgroundColor}
      />
    </SectionContainer>
  );
};

interface PageBuilderCustomOfferBoxProps
  extends PageBuilderCustomOfferBoxFragment {
  cardBackgroundColor?: BackgroundColor;
}

const PageBuilderCustomOfferBox: React.FC<
  React.PropsWithChildren<PageBuilderCustomOfferBoxProps>
> = ({ title, subtitle, disclaimer, items, cardBackgroundColor }) => {
  const offersRemote = useRemotePromos();

  const offers = (items || [])
    .map((i) => {
      if (!isPresent(i)) return undefined;
      const promoContent = i.promo;
      const remotePromo = offersRemote?.data?.find(
        byConfigurationCode(promoContent?.configuration?.code)
      );
      const overrideDescription = i.text?.localized;
      if (
        isPresent(promoContent?.configuration?.code) &&
        !isPresent(remotePromo)
      ) {
        return undefined;
      }

      return {
        name: i.title?.localized || i.promo?.promoFeatureTitle?.localized || "",
        description:
          (isBlockContentValuePresent(overrideDescription)
            ? overrideDescription
            : promoContent?.card?.blockDescription?.localized) || "",
        icon: i.icon || (promoContent ? "default" : undefined),
        iconUrl: promoContent?.iconVertical?.asset?.url,
        tokenValues: remotePromo?.tokenValues
      } satisfies OfferHighlight;
    })
    .filter(isPresent);

  return (
    <React.Fragment>
      <SectionTitle variant="wide">
        <SectionTitleHeading tagline={subtitle?.localized}>
          {title?.localized}
        </SectionTitleHeading>
        <SectionTitleBody>
          <OfferHighlights
            variant="large"
            offers={offers}
            title={undefined}
            tokenValues={{}}
            cardBackgroundColor={cardBackgroundColor}
            defaultOfferIconUrl={undefined}
          />
          {disclaimer?.localized && (
            <React.Fragment>
              <br />
              <TextBox
                content={disclaimer?.localized}
                fontSize="disclaimer"
                textAlign="left"
              />
            </React.Fragment>
          )}
        </SectionTitleBody>
      </SectionTitle>
    </React.Fragment>
  );
};

// eslint-disable-next-line import/no-unused-modules
export const fragments = graphql`
  fragment PageBuilderCustomOfferBoxItem on SanityCustomOfferBoxItem {
    _key
    icon
    title {
      localized
    }
    text {
      localized
    }
    promo {
      configuration {
        code
      }

      promoFeatureTitle: featureTitle {
        localized
      }

      card {
        blockDescription: description {
          localized
        }
      }

      iconVertical {
        asset {
          url
        }
      }
    }
  }
  fragment PageBuilderCustomOfferBox on SanityCustomOfferBox {
    _key
    title {
      localized
    }
    subtitle {
      localized
    }
    disclaimer {
      localized
    }
    locationFilters {
      ...LocationFilter
    }
    items {
      ...PageBuilderCustomOfferBoxItem
    }
  }
`;
